/**
 * The route component of BOTH /app/workflow2-create (CreateWorkflow2) and /app/workflow2-edit/:workflowID
 * (ViewWorkflow2) (Workflow overhaul P5, 2026-10-10). Resume doc: Documents/workflow-ui-overhaul.md.
 *
 * Renders EXACTLY ONE builder:
 *   redesign/WorkflowBuilderPage.vue   the redesigned builder (design frames 1c-1f)
 *   components/WorkflowBuilder.vue     the classic builder, NOT edited
 * decided by shouldUseNewFollowupsUi('workflow', { isSuperAdmin, userKey, storage }): at stage `preview` a Super
 * Admin gets the new builder unless they picked "Classic view" (localStorage teamio_workflow_ui = 'old') and
 * everyone else gets the classic one. Later stages and the office "classic Teamio" choice are the gate's
 * business (src/utils/followups/uiVersion.js), not this file's. Same shape as WorkflowListRoute.js and
 * src/pages/PatientProfile/PatientProfileRoute.js: a .js render function, so each builder stays the page root
 * it really is, plus a version counter that makes the gate re-read storage after a Classic / New click.
 *
 * ⛔ `userKey` MUST be passed (the employee ID the office choice is stored under; `state.customer.userID`).
 *
 * ── THREE THINGS A WRAPPER HAS TO DO THAT A PLAIN ROUTE PAGE DOES NOT ───────────────────────────────
 *
 * 1. KEY THE PAGE BY WORKFLOW. Create and Edit share this one component, so the router REUSES the instance
 *    when it goes create -> edit or edit A -> edit B; a page's created() would not run again and it would keep
 *    showing the old workflow. `key` (`new-<id>` / `old-<id>`, `create` when there is no ID) forces a fresh
 *    page per workflow.
 *
 * 2. FORWARD THE LEAVE GUARDS. Vue Router calls beforeRouteLeave / beforeRouteUpdate only on the component the
 *    route matched, and that is now THIS wrapper, so without forwarding the unsaved-changes prompt is lost.
 *    The new page answers with its inline `requestLeave(next)`; the classic page keeps its own
 *    `beforeRouteLeave` option, which is run with the classic page instance as `this`. beforeRouteUpdate
 *    (edit A -> edit B) resolves BEFORE the key changes, because the router only swaps the params after the
 *    guards have said go.
 *
 * 3. THE CHAIN FALLBACK. A workflow saved as a chain (an action hanging off another action) cannot be drawn by
 *    the new builder, which then emits `needs-classic`. This wrapper remembers `{ workflowID, reason }`; while
 *    that matches the workflow on screen it draws the CLASSIC builder (even for a Super Admin on the new
 *    layout) with a one-line note instead of the New-view link. Going to another workflow, or to create,
 *    forgets it.
 *
 * Switching layout (the new page's `switch-layout`, the classic-side link's `toggle`) asks the SAME way a leave
 * does when the page being replaced has unsaved changes, and only switches when the answer is "go".
 */
import WorkflowBuilder from './components/WorkflowBuilder.vue';
import WorkflowBuilderPage from './redesign/WorkflowBuilderPage.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';
import { switchUi } from '@/utils/followups/uiSwitch.js';

const CHAIN_NOTE = 'This workflow runs its steps in order, which the new builder can\'t show yet, so it opens in the classic builder.';
const TOGGLE_NOTE = 'Super Admin: a redesigned workflow builder is ready to check.';

/**
 * Run a component's guard option (a function, or the array Vue Router's merge strategy makes of it) one after
 * the other with `ctx` as `this`. The first guard that answers anything but "go" ends the run.
 */
function runGuards(guards, ctx, to, from, next) {
  const list = (Array.isArray(guards) ? guards : [guards]).filter(g => typeof g === 'function');
  let i = 0;
  const step = (answer) => {
    if (answer !== undefined) { next(answer); return; }
    if (i >= list.length) { next(); return; }
    const guard = list[i];
    i += 1;
    guard.call(ctx, to, from, step);
  };
  step();
}

export default {
  name: 'WorkflowBuilderRoute',

  props: {
    // The router passes both (`props: true`). `openTab` is an in-memory param, never in the URL.
    workflowID: { type: String, default: null },
    openTab: { type: String, default: '' },
  },

  data() {
    // layoutVersion is bumped on every layout switch so the gate re-reads storage;
    // chain is { workflowID, reason } once the new builder said a workflow has to open in the classic one.
    return { layoutVersion: 0, chain: null };
  },

  computed: {
    isSuperAdminUser() {
      const user = this.$store.state.customer && this.$store.state.customer.user;
      return !!(user && user.Role === 'Super Admin');
    },
    // The employee ID the office choice is stored under (a route component has no mixin).
    officeUserKey() {
      return this.$store.state.customer ? this.$store.state.customer.userID : null;
    },
    useNew() {
      // eslint-disable-next-line no-unused-expressions
      this.layoutVersion;
      return shouldUseNewFollowupsUi('workflow', { isSuperAdmin: this.isSuperAdminUser, userKey: this.officeUserKey, storage: browserStorage() });
    },
    // '' for the create route, so a chain note can never leak onto it.
    currentID() {
      return this.workflowID || '';
    },
    chainForcesClassic() {
      return !!this.chain && this.chain.workflowID === this.currentID;
    },
  },

  watch: {
    // Another workflow (or create) is on screen: forget the chain verdict of the last one.
    workflowID() {
      this.chain = null;
    },
  },

  methods: {
    onNeedsClassic(workflowID, reason) {
      this.chain = { workflowID, reason };
    },

    /**
     * Ask the drawn page whether it may go: the new page asks inline (requestLeave), the classic page runs its own
     * beforeRouteLeave. `next` is a router `next`: next() = go, next(false) = stay. No page = go.
     */
    askPage(to, from, next) {
      const page = this.$refs.page;
      if (!page) { next(); return; }
      if (typeof page.requestLeave === 'function') { page.requestLeave(next); return; }
      const guard = page.$options && page.$options.beforeRouteLeave;
      if (!guard) { next(); return; }
      runGuards(guard, page, to, from, next);
    },

    // A Classic / New click. A Super Admin re-renders in place; an office user's switch reloads the page.
    requestSwitch(target) {
      this.askPage(null, this.$route || null, (answer) => {
        if (answer === false) return;
        const how = switchUi('workflow', target, { isSuperAdmin: this.isSuperAdminUser, userKey: this.officeUserKey });
        if (how === 'rerender') this.layoutVersion += 1;
      });
    },
  },

  // Vue Router calls these only on the matched component, which is this wrapper: forward them to the page.
  beforeRouteLeave(to, from, next) {
    this.askPage(to, from, next);
  },

  // edit A -> edit B on the same route record. Resolved before the router swaps the params, so before the key
  // below replaces the page.
  beforeRouteUpdate(to, from, next) {
    this.askPage(to, from, next);
  },

  render(h) {
    const id = this.currentID;
    const keyID = id || 'create';
    if (this.useNew && !this.chainForcesClassic) {
      return h(WorkflowBuilderPage, {
        ref: 'page',
        key: `new-${keyID}`,
        props: { workflowID: this.workflowID, openTab: this.openTab },
        on: {
          'switch-layout': this.requestSwitch,
          'needs-classic': reason => this.onNeedsClassic(id, reason),
        },
      });
    }
    const children = [];
    if (this.chainForcesClassic) {
      children.push(h('div', {
        key: 'chain-note',
        style: { marginBottom: '10px', textAlign: 'right', fontSize: '12.5px', color: '#5b6770' },
        attrs: { 'data-test': 'workflow-chain-note', role: 'status' },
      }, CHAIN_NOTE));
    } else {
      // The way to the new builder from the classic one. FollowupsUiToggle decides by role itself: always shown to
      // a Super Admin, shown to an office user only while the area is at 'invite' / 'default'.
      children.push(h(FollowupsUiToggle, {
        key: 'to-new',
        style: { marginBottom: '10px' },
        props: { target: 'new', pageKey: 'workflow', noteText: TOGGLE_NOTE },
        on: { toggle: this.requestSwitch },
      }));
    }
    children.push(h(WorkflowBuilder, {
      ref: 'page',
      key: `old-${keyID}`,
      props: { workflowID: this.workflowID },
    }));
    return h('div', { attrs: { 'data-test': 'workflow-builder-classic' } }, children);
  },
};
